import { format } from 'date-fns';

import { ZoomWebinarOccurrence, ZoomWebinarRecurrence, ZoomWebinarRecurrenceType } from 'types/zoom';

import RecurrenceIntervalAndTime from '../types/recurrence-interval-time';

export function formatDate(date: Date): string {
  return format(date, 'EEEE, MMMM d, y');
}

export function formatTime(date: Date): string {
  return format(date, 'h:mmaaa');
}

export function getUserTimeZone(variant?: 'long' | 'short'): string | null {
  const dateNow = new Date();
  const localTimes = dateNow.toLocaleTimeString('en-us', { timeZoneName: variant }).split(' ');

  if (localTimes && localTimes.length < 3) {
    // guard against timeZoneName not being supported (in IE 11)
    return null;
  }

  return localTimes[2];
}

function getWeekOfMonthValue(monthlyWeek: number): string {
  switch (monthlyWeek) {
    case -1:
      return 'last';
    case 1:
      return 'first';
    case 2:
      return 'second';
    case 3:
      return 'third';
    case 4:
      return 'fourth';
    default:
      return '';
  }
}

type DayOfWeek = {
  full: string
  short: string
}
const daysOfWeek: DayOfWeek[] = [
  {
    full: 'Sunday',
    short: 'Sun',
  }, {
    full: 'Monday',
    short: 'Mon',
  }, {
    full: 'Tuesday',
    short: 'Tue',
  }, {
    full: 'Wednesday',
    short: 'Wed',
  }, {
    full: 'Thursday',
    short: 'Thu',
  }, {
    full: 'Friday',
    short: 'Fri',
  }, {
    full: 'Saturday',
    short: 'Sat',
  },
];

export function determineRecurringIntervalAndTime(
  data: ZoomWebinarRecurrence,
  nextOccurrence: ZoomWebinarOccurrence,
  omitTimezone = false
): RecurrenceIntervalAndTime {

  if (!nextOccurrence) {
    return {
      interval: '',
      intervalValue: '',
      time: null,
    };
  }

  let interval = 'a recurring';
  let intervalValue = 'Register';
  let time: string | null = null;

  const nextOccurenceTime = `${formatTime(new Date(nextOccurrence.startTime))} ${!omitTimezone ? getUserTimeZone('short') : ''}`;

  if (data.type === ZoomWebinarRecurrenceType.DAILY) {
    if (data.repeatInterval === 1) {
      interval = 'a daily';
      intervalValue = 'Daily';
    } else {
      intervalValue = 'Select Days';
    }
    time = nextOccurenceTime;

  } else if (data.type === ZoomWebinarRecurrenceType.WEEKLY && data.weeklyDays) {

    const daysNumbers = data.weeklyDays.split(',')
      .map(day => parseInt(day))
      .sort((a, b) => a - b);
    const days = daysNumbers.map(dayNumber => daysOfWeek[dayNumber - 1]);

    if (days.length === 1) {
      intervalValue = `${days[0].full}s`;
    } else if (days.length === 2) {
      intervalValue = `${days[0].short} and ${days[1].short}`;
    } else {
      intervalValue = 'Select Days';
    }

    if (data.repeatInterval === 1) {
      interval = 'a weekly';
    } else if (data.repeatInterval === 2) {
      interval = 'a semi-weekly';
    }

    time = nextOccurenceTime;
    
  } else if (data.type === ZoomWebinarRecurrenceType.MONTHLY) {

    if (data.repeatInterval === 1) {
      interval = 'a monthly';
      
      if (data.monthlyDay) {
        const date = new Date();
        date.setDate(data.monthlyDay);
        intervalValue = `The ${format(date, 'do')} of every month`;
        time = nextOccurenceTime;
      } else if (data.monthlyWeek && data.monthlyWeekDay) {
        const week = getWeekOfMonthValue(data.monthlyWeek);
        const weekDay = daysOfWeek[data.monthlyWeekDay - 1].full;
        intervalValue = `The ${week} ${weekDay} of every month`;
        time = nextOccurenceTime;
      }

    } else if (data.repeatInterval === 2) {
      interval = 'a semi-monthly';

      if (data.monthlyDay) {
        const date = new Date();
        date.setDate(data.monthlyDay);
        intervalValue = `The ${format(date, 'do')} of every other month`;
        time = nextOccurenceTime;
      } else if (data.monthlyWeek && data.monthlyWeekDay) {
        const week = getWeekOfMonthValue(data.monthlyWeek);
        const weekDay = daysOfWeek[data.monthlyWeekDay - 1].full;
        intervalValue = `The ${week} ${weekDay} of every other month`;
        time = nextOccurenceTime;
      }
    }

  }

  return {
    interval,
    intervalValue,
    time,
  };
}